<template>
  <div class="contact-page">
    <!-- Header -->
    <div class="contact-header">
      <div class="container">
        <h1 class="contact-title">Contact Us</h1>
        <p class="contact-subtitle">
          Have questions about finding the right franchise or listing your franchise opportunity? We're here to help!
        </p>
      </div>
    </div>
    
    <!-- Contact info and form -->
    <div class="contact-content">
      <div class="contact-grid">
        <div class="contact-info">
          <h2 class="section-title">Get in Touch</h2>
          
          <div class="contact-details">
            <div class="contact-item">
              <div class="contact-icon">
                <span class="icon-text">📍</span>
              </div>
              <div class="contact-text">
                <h3>Our Office</h3>
                <p>{{ address || 'Janardhan Towers, Residency Road, Above IOB Bank, Opp Bishop Cotton Boys School, Bangalore - 560025' }}</p>
              </div>
            </div>
            
            <div class="contact-item">
              <div class="contact-icon">
                <span class="icon-text">✉️</span>
              </div>
              <div class="contact-text">
                <h3>Email Us</h3>
                <p>
                  <a :href="`mailto:${email || 'info@franchisebrigade.com'}`" class="contact-link">
                    {{ email || 'info@franchisebrigade.com' }}
                  </a>
                </p>
              </div>
            </div>
            
            <div class="contact-item">
              <div class="contact-icon">
                <span class="icon-text">📞</span>
              </div>
              <div class="contact-text">
                <h3>Call Us</h3>
                <p>
                  <a :href="`tel:${phone || '+91 9606943373'}`" class="contact-link">
                    {{ phone || '+91 9606943373' }}
                  </a>
                </p>
              </div>
            </div>
          </div>
          
          <div class="business-hours">
            <h3 class="hours-title">Our Business Hours</h3>
            <p class="hours-desc">Below are our working hours. You can reach us during these times for any queries or support.</p>
            <ul class="hours-list">
              <li v-for="(item, idx) in groupedBusinessHours" :key="idx" class="hours-item">
                <span class="hours-days">{{ formatDays(item.days) }}:</span>
                <span class="hours-time">{{ item.hours }}</span>
              </li>
              <li v-if="groupedBusinessHours.length === 0" class="hours-item">
                <span class="hours-days">Mon - Fri:</span>
                <span class="hours-time">9:00 AM - 6:00 PM</span>
              </li>
              <li v-if="groupedBusinessHours.length === 0" class="hours-item">
                <span class="hours-days">Sat:</span>
                <span class="hours-time">10:00 AM - 4:00 PM</span>
              </li>
              <li v-if="groupedBusinessHours.length === 0" class="hours-item">
                <span class="hours-days">Sun:</span>
                <span class="hours-time">Closed</span>
              </li>
            </ul>
          </div>

          <!-- Social Media Links -->
          <div class="social-links">
            <a v-if="links.facebook && links.facebook !== '#'" :href="links.facebook" class="social-link" target="_blank" rel="noopener noreferrer">
              <i class="fab fa-facebook-f"></i>
            </a>
            <a v-if="links.twitter && links.twitter !== '#'" :href="links.twitter" class="social-link" target="_blank" rel="noopener noreferrer">
              <i class="fab fa-twitter"></i>
            </a>
            <a v-if="links.instagram && links.instagram !== '#'" :href="links.instagram" class="social-link" target="_blank" rel="noopener noreferrer">
              <i class="fab fa-instagram"></i>
            </a>
            <a v-if="links.linkedin && links.linkedin !== '#'" :href="links.linkedin" class="social-link" target="_blank" rel="noopener noreferrer">
              <i class="fab fa-linkedin-in"></i>
            </a>
          </div>
        </div>
        
        <div class="contact-form-container">
          <div class="contact-form-card">
            <h2 class="section-title">Send Us a Message</h2>
            
            <div v-if="submitted" class="success-message">
              <div class="success-icon">
                <i class="fas fa-check"></i>
              </div>
              <h3 class="success-title">Message Sent Successfully!</h3>
              <p class="success-desc">
                Thank you for contacting us. We'll get back to you as soon as possible.
              </p>
            </div>
            
            <form v-else @submit.prevent="handleSubmit" class="contact-form">
              <div v-if="errors.form" class="error-message">
                {{ errors.form }}
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <label for="name" class="form-label">Your Name *</label>
                  <div class="input-container">
                    <span class="input-icon">👤</span>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      v-model="formData.name"
                      @input="handleChange"
                      :class="['form-input', { 'error': errors.name }]"
                      required
                    />
                  </div>
                  <p v-if="errors.name" class="error-text">{{ errors.name }}</p>
                </div>
                
                <div class="form-group">
                  <label for="email" class="form-label">Email Address *</label>
                  <div class="input-container">
                    <span class="input-icon">✉️</span>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      v-model="formData.email"
                      @input="handleChange"
                      :class="['form-input', { 'error': errors.email }]"
                      required
                    />
                  </div>
                  <p v-if="errors.email" class="error-text">{{ errors.email }}</p>
                </div>
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <label for="phone" class="form-label">Phone Number *</label>
                  <div class="input-container">
                    <span class="input-icon">📞</span>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      v-model="formData.phone"
                      @input="handleChange"
                      :class="['form-input', { 'error': errors.phone }]"
                      required
                    />
                  </div>
                  <p v-if="errors.phone" class="error-text">{{ errors.phone }}</p>
                </div>
                
                <div class="form-group">
                  <label for="subject" class="form-label">Subject *</label>
                  <div class="input-container">
                    <span class="input-icon">🏢</span>
                    <select
                      id="subject"
                      name="subject"
                      v-model="formData.subject"
                      @change="handleChange"
                      :class="['form-input', { 'error': errors.subject }]"
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="Franchise Inquiry">Franchise Inquiry</option>
                      <option value="List My Franchise">List My Franchise</option>
                      <option value="Partnership Opportunity">Partnership Opportunity</option>
                      <option value="Support">Support</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <p v-if="errors.subject" class="error-text">{{ errors.subject }}</p>
                </div>
              </div>
              
              <div class="form-group">
                <label for="message" class="form-label">Your Message *</label>
                <div class="input-container">
                  <i class="fa fa-comment input-icon textarea-icon"></i>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    v-model="formData.message"
                    @input="handleChange"
                    :class="['form-input', 'form-textarea', { 'error': errors.message }]"
                    required
                  ></textarea>
                </div>
                <p v-if="errors.message" class="error-text">{{ errors.message }}</p>
              </div>
              
              <button type="submit" class="submit-btn" :disabled="submitting">
                {{ submitting ? 'Sending...' : 'Send Message' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Map -->
    <div class="map-container">
      <div class="map-placeholder">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.9619409816414!2d77.6069325!3d12.974286099999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1597751ee201%3A0xde8beaaaa7e02e3e!2sFranchise%20Brigade!5e0!3m2!1sen!2sin!4v1751972691209!5m2!1sen!2sin"
          style="border:0; width:100%; height:100%; border-radius:16px; min-height:300px;"
          allowfullscreen=""
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useContactInfoStore } from '@/stores/contactInfoStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useBusinessHoursStore } from '@/stores/businessHoursStore'

const contactInfoStore = useContactInfoStore()
const settingsStore = useSettingsStore()
const businessHoursStore = useBusinessHoursStore()

// Reactive data
const formData = reactive({
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
})

const submitted = ref(false)
const submitting = ref(false)
const errors = reactive({})

// Contact info from stores
const address = computed(() => contactInfoStore.contactInfo?.address || 'Janardhan Towers, Residency Road, Above IOB Bank, Opp Bishop Cotton Boys School, Bangalore - 560025')
const email = computed(() => contactInfoStore.contactInfo?.email || 'info@franchisebrigade.com')
const phone = computed(() => contactInfoStore.contactInfo?.phone || '+91 9606943373')

// Social links from settings store
const links = computed(() => ({
  facebook: settingsStore.settings?.social_facebook || '#',
  twitter: settingsStore.settings?.social_twitter || '#',
  instagram: settingsStore.settings?.social_instagram || '#',
  linkedin: settingsStore.settings?.social_linkedin || '#'
}))

// Computed properties
const groupedBusinessHours = computed(() => {
  return businessHoursStore.groupedBusinessHours
})

// Methods
const handleChange = (e) => {
  const { name, value } = e.target
  formData[name] = value
  if (errors[name]) {
    errors[name] = ''
  }
}

const validateForm = () => {
  const newErrors = {}
  
  if (!formData.name.trim()) newErrors.name = 'Name is required'
  if (!formData.email.trim()) newErrors.email = 'Email is required'
  else if (!validateEmail(formData.email)) newErrors.email = 'Invalid email format'
  if (!formData.phone.trim()) newErrors.phone = 'Phone is required'
  else if (!validatePhone(formData.phone)) newErrors.phone = 'Invalid phone format'
  if (!formData.subject.trim()) newErrors.subject = 'Subject is required'
  if (!formData.message.trim()) newErrors.message = 'Message is required'
  
  Object.assign(errors, newErrors)
  return Object.keys(newErrors).length === 0
}

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const validatePhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
  return phoneRegex.test(phone.replace(/\s/g, ''))
}

const handleSubmit = async (e) => {
  e.preventDefault()
  
  if (!validateForm()) return
  
  submitting.value = true
  try {
    const API_BASE_URL = 'https://franchisebrigade.com/admin/api'
    const response = await fetch(`${API_BASE_URL}/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    submitted.value = true
    Object.assign(formData, { name: '', email: '', phone: '', subject: '', message: '' })
    setTimeout(() => {
      submitted.value = false
    }, 2000)

  } catch (err) {
    errors.form = 'Failed to send message. Please try again.'
  } finally {
    submitting.value = false
  }
}

const formatDays = (days) => {
  if (days.length === 1) return dayShort(days[0])
  
  const weekOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  const sorted = days.slice().sort((a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b))
  
  const result = []
  let start = 0
  for (let i = 1; i <= sorted.length; i++) {
    if (i === sorted.length || weekOrder.indexOf(sorted[i]) !== weekOrder.indexOf(sorted[i-1]) + 1) {
      if (start === i-1) result.push(dayShort(sorted[start]))
      else result.push(`${dayShort(sorted[start])} - ${dayShort(sorted[i-1])}`)
      start = i
    }
  }
  return result.join(', ')
}

const dayShort = (day) => {
  const map = {
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
    Sunday: 'Sun'
  }
  return map[day] || day
}

// Lifecycle
onMounted(() => {
  contactInfoStore.fetchContactInfo()
  settingsStore.fetchSettings()
  businessHoursStore.fetchBusinessHours()
})
</script>

<style scoped>
.contact-page {
  font-family: 'Inter', Arial, sans-serif;
  color: #222;
  background: #f8f9fb;
  min-height: 100vh;
}

/* Header */
.contact-header {
  background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%);
  color: #fff;
  padding: clamp(80px, 12vw, 120px) 16px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.contact-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 1;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  position: relative;
  z-index: 2;
}

.contact-title {
  font-size: clamp(3.5rem, 10vw, 6rem);
  font-weight: 900;
  margin-bottom: clamp(24px, 4vw, 32px);
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.contact-subtitle {
  font-size: clamp(1.5rem, 4vw, 2.25rem);
  color: #bfdbfe;
  max-width: 768px;
  margin: 0 auto;
  line-height: 1.6;
  opacity: 0.95;
}

/* Content */
.contact-content {
  padding: clamp(80px, 12vw, 120px) 16px;
}

.contact-grid {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(48px, 8vw, 80px);
}

@media (min-width: 1024px) {
  .contact-grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* Contact Info */
.contact-info {
  order: 2;
}

@media (min-width: 1024px) {
  .contact-info {
    order: 1;
  }
}

.section-title {
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  font-weight: 800;
  color: #18181b;
  margin-bottom: clamp(32px, 5vw, 40px);
  line-height: 1.2;
}

.contact-details {
  margin-bottom: clamp(40px, 6vw, 48px);
}

.contact-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: clamp(32px, 5vw, 40px);
  padding: clamp(20px, 4vw, 24px) 0;
}

.contact-icon {
  background: #fff;
  border-radius: 50%;
  padding: clamp(20px, 4vw, 24px);
  margin-right: clamp(20px, 4vw, 24px);
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.08);
  min-width: clamp(60px, 8vw, 72px);
  min-height: clamp(60px, 8vw, 72px);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #e5e7eb;
}

.contact-icon i {
  color: #2563eb;
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 600;
}

.contact-text h3 {
  font-size: clamp(1.375rem, 3vw, 1.75rem);
  font-weight: 700;
  color: #18181b;
  margin-bottom: 12px;
}

.contact-text p {
  color: #444;
  line-height: 1.6;
  font-size: clamp(1.125rem, 2.5vw, 1.375rem);
}

.contact-link {
  color: #6b7280;
  text-decoration: none;
  transition: color 0.3s ease;
  font-weight: 500;
  font-size: clamp(1.125rem, 2.5vw, 1.375rem);
}

.contact-link:hover {
  color: #2563eb;
}

/* Business Hours */
.business-hours {
  background: #f8fafc;
  border-radius: 16px;
  padding: clamp(32px, 5vw, 40px);
  margin-bottom: clamp(32px, 5vw, 40px);
  border: 1px solid #e2e8f0;
}

.hours-title {
  font-size: clamp(1.5rem, 3.5vw, 2rem);
  font-weight: 700;
  color: #18181b;
  margin-bottom: clamp(16px, 3vw, 20px);
}

.hours-desc {
  color: #444;
  font-size: clamp(1rem, 2.25vw, 1.25rem);
  margin-bottom: clamp(20px, 3vw, 24px);
  line-height: 1.6;
}

.hours-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.hours-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: clamp(12px, 2.5vw, 16px);
  padding: clamp(12px, 2.5vw, 16px) 0;
  border-bottom: 1px solid #e5e7eb;
}

.hours-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.hours-days {
  color: #6b7280;
  font-weight: 600;
  font-size: clamp(1rem, 2.25vw, 1.25rem);
}

.hours-time {
  color: #6b7280;
  font-size: clamp(1rem, 2.25vw, 1.25rem);
}

/* Social Links */
.social-links {
  display: flex;
  gap: clamp(20px, 4vw, 24px);
  margin-top: clamp(24px, 4vw, 32px);
}

.social-link {
  color: #6b7280;
  font-size: clamp(2rem, 5vw, 2.5rem);
  transition: all 0.3s ease;
  padding: clamp(16px, 3.5vw, 20px);
  border-radius: 50%;
  background: #fff;
  border: 2px solid #e5e7eb;
  min-width: clamp(60px, 8vw, 72px);
  min-height: clamp(60px, 8vw, 72px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.social-link:hover {
  color: #2563eb;
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.3);
  border-color: #2563eb;
}

/* Contact Form */
.contact-form-container {
  order: 1;
}

@media (min-width: 1024px) {
  .contact-form-container {
    order: 2;
  }
}

.contact-form-card {
  background: #fff;
  border-radius: 16px;
  padding: clamp(40px, 7vw, 48px);
  border: 1px solid #e5e7eb;
}

/* Form Styles */
.contact-form {
  margin-top: clamp(32px, 5vw, 40px);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(24px, 4vw, 32px);
  margin-bottom: clamp(24px, 4vw, 32px);
}

@media (min-width: 768px) {
  .form-row {
    grid-template-columns: 1fr 1fr;
  }
}

.form-group {
  margin-bottom: clamp(24px, 4vw, 32px);
}

.form-label {
  display: block;
  font-size: clamp(1.1rem, 2.5vw, 1.25rem);
  font-weight: 700;
  color: #18181b;
  margin-bottom: 12px;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.input-container .input-icon {
  position: absolute;
  left: clamp(20px, 4vw, 24px);
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: clamp(1.5rem, 3.5vw, 1.875rem);
  pointer-events: none;
  font-weight: 500;
  z-index: 2;
}

.input-container select.form-input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: #fff;
  color: #18181b;
  font-size: clamp(1.125rem, 2.5vw, 1.375rem);
  font-family: inherit;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  width: 100%;
  padding: clamp(20px, 4vw, 24px) clamp(40px, 6vw, 48px) clamp(20px, 4vw, 24px) clamp(56px, 8vw, 64px);
  transition: all 0.3s ease;
  box-sizing: border-box;
  outline: none;
  font-weight: 400;
  line-height: 1.2;
  display: block;
}

.input-container select.form-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
}

.input-container select.form-input option {
  font-weight: 400;
  color: #18181b;
}

.textarea-icon {
  top: clamp(24px, 5vw, 28px);
  transform: none;
}

.form-input,
.input-container select.form-input,
.form-textarea {
  color: #18181b;
  font-weight: 600;
  font-size: clamp(1.125rem, 2.5vw, 1.375rem);
  background: #fff;
}

.form-input::placeholder,
.input-container select.form-input::placeholder,
.form-textarea::placeholder {
  color: #6b7280;
  opacity: 1;
  font-weight: 500;
}

.form-input {
  width: 100%;
  padding: clamp(20px, 4vw, 24px) clamp(20px, 4vw, 24px) clamp(20px, 4vw, 24px) clamp(60px, 9vw, 72px);
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
}

.form-input:focus + .input-icon {
  color: #2563eb;
}

.form-input.error {
  border-color: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

.form-textarea {
  padding-top: clamp(20px, 4vw, 24px);
  padding-left: clamp(60px, 9vw, 72px);
  resize: vertical;
  min-height: 140px;
  line-height: 1.6;
}

.error-text {
  color: #ef4444;
  font-size: clamp(0.875rem, 2vw, 1rem);
  margin-top: 8px;
  font-weight: 500;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  padding: clamp(16px, 3vw, 20px);
  border-radius: 12px;
  margin-bottom: clamp(20px, 3vw, 24px);
  border: 1px solid #fecaca;
  font-weight: 500;
  font-size: clamp(1rem, 2.25vw, 1.25rem);
}

.submit-btn {
  width: 100%;
  background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: clamp(20px, 4vw, 24px) clamp(32px, 5vw, 40px);
  font-size: clamp(1.125rem, 2.5vw, 1.375rem);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
  position: relative;
  overflow: hidden;
}

.submit-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.submit-btn:hover:not(:disabled)::before {
  left: 100%;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.4);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Success Message */
.success-message {
  text-align: center;
  padding: clamp(48px, 9vw, 72px) clamp(24px, 5vw, 40px);
}

.success-icon {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  border-radius: 50%;
  width: clamp(96px, 18vw, 120px);
  height: clamp(96px, 18vw, 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto clamp(24px, 5vw, 32px);
  box-shadow: 0 8px 20px rgba(34, 197, 94, 0.4);
}

.success-icon i {
  color: #16a34a;
  font-size: clamp(3rem, 6vw, 3.75rem);
  font-weight: 600;
}

.success-title {
  font-size: clamp(1.5rem, 3.5vw, 2rem);
  font-weight: 700;
  color: #18181b;
  margin-bottom: clamp(12px, 2.5vw, 16px);
}

.success-desc {
  color: #6b7280;
  line-height: 1.6;
  font-size: clamp(1.125rem, 2.5vw, 1.375rem);
}

/* Map */
.map-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(40px, 7vw, 56px) 16px;
}

.map-placeholder {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  height: clamp(300px, 50vw, 400px);
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
}

.map-loading {
  color: #6b7280;
  font-size: clamp(1.375rem, 3vw, 1.5rem);
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .contact-content {
    padding: clamp(48px, 8vw, 64px) 16px;
  }
  
  .contact-form-card {
    padding: clamp(32px, 6vw, 40px);
  }
  
  .business-hours {
    padding: clamp(24px, 5vw, 32px);
  }
  
  .contact-item {
    padding: clamp(20px, 4vw, 24px) 0;
  }
}

@media (max-width: 480px) {
  .contact-header {
    padding: clamp(60px, 10vw, 80px) 16px;
  }
  
  .contact-content {
    padding: clamp(40px, 6vw, 48px) 16px;
  }
  
  .contact-form-card {
    padding: clamp(24px, 5vw, 32px);
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>